import { GradientParser } from '../lib/gradient';
import { TYPES } from './constants';

export const MARKER_TYPES = {
  none: 'none',
  arrow: 'arrow',
  circle: 'circle',
  square: 'square',
};

export function renderLine(renderContext, node) {
  if (node.type !== TYPES.line) return;
  const { context } = renderContext;

  // 获取线段配置
  const {
    size = 1,
    strokeStyle = 'solid', // 'solid' | 'dashed'
    dashArray = '',
    fill = '#000000',
    opacity = 1,
    lineCap = 'butt', // 'butt' | 'round' | 'square'
    lineJoin = 'miter', // 'miter' | 'round' | 'bevel'
    startMarker = MARKER_TYPES.none, // 'none' | 'arrow' | 'circle' | 'square'
    endMarker = MARKER_TYPES.none, // 'none' | 'arrow' | 'circle' | 'square'
    markerSize = 10, // 标记大小
  } = node;

  const actualStartMarker = startMarker;
  const actualEndMarker = endMarker;
  const actualMarkerSize = markerSize;

  const points = deserializePoints(node.points);

  // 如果没有点或点数少于2个，不绘制
  if (!points || points.length < 2) return;

  context.save();
  context.translate(node.x, node.y);

  // 设置透明度
  if (opacity !== undefined && opacity !== 1) {
    context.globalAlpha = opacity;
  }

  // 设置线条样式
  context.lineWidth = size;
  context.lineCap = lineCap;
  context.lineJoin = lineJoin;

  // 设置虚线样式
  if (strokeStyle === 'dashed') {
    const arr = deserializeDashArray(dashArray);
    context.setLineDash(arr);
  } else {
    context.setLineDash([]);
  }

  // 转换相对坐标为绝对坐标
  const absolutePoints = points.map((point) => ({
    x: point.x * node.w,
    y: point.y * node.h,
  }));

  // 计算标记位置和角度
  const adjustedPoints = [...absolutePoints];

  if (actualStartMarker !== MARKER_TYPES.none && adjustedPoints.length >= 2) {
    const start = adjustedPoints[0];
    const second = adjustedPoints[1];
    const angle = Math.atan2(second.y - start.y, second.x - start.x);

    // 根据标记类型调整起始点位置
    const markerOffset = getMarkerOffset(actualStartMarker, actualMarkerSize);
    adjustedPoints[0] = {
      x: start.x + Math.cos(angle) * markerOffset,
      y: start.y + Math.sin(angle) * markerOffset,
    };
  }

  if (actualEndMarker !== MARKER_TYPES.none && adjustedPoints.length >= 2) {
    const end = adjustedPoints[adjustedPoints.length - 1];
    const beforeEnd = adjustedPoints[adjustedPoints.length - 2];
    const angle = Math.atan2(end.y - beforeEnd.y, end.x - beforeEnd.x);

    // 根据标记类型调整结束点位置
    const markerOffset = getMarkerOffset(actualEndMarker, actualMarkerSize);
    adjustedPoints[adjustedPoints.length - 1] = {
      x: end.x - Math.cos(angle) * markerOffset,
      y: end.y - Math.sin(angle) * markerOffset,
    };
  }

  // 开始绘制路径
  context.beginPath();

  // 移动到第一个点
  const firstPoint = adjustedPoints[0];
  context.moveTo(firstPoint.x, firstPoint.y);

  // 连接所有点
  for (let i = 1; i < adjustedPoints.length; i++) {
    const point = adjustedPoints[i];
    context.lineTo(point.x, point.y);
  }

  // 设置颜色或渐变
  const finalStrokeColor = fill;

  if (GradientParser.is(finalStrokeColor)) {
    // 计算渐变区域（使用节点的宽高作为边界）
    const bounds = { x: 0, y: 0, w: node.w, h: node.h };
    GradientParser.create(context, finalStrokeColor, bounds, (color) => {
      context.strokeStyle = color;
      context.stroke();
    });
  } else {
    context.strokeStyle = finalStrokeColor;
    context.stroke();
  }

  // 绘制标记
  context.fillStyle = finalStrokeColor;
  context.strokeStyle = finalStrokeColor;

  // 绘制起始标记
  if (actualStartMarker !== MARKER_TYPES.none && absolutePoints.length >= 2) {
    drawMarker(
      context,
      absolutePoints[0],
      absolutePoints[1],
      actualMarkerSize,
      actualStartMarker,
      true,
    );
  }

  // 绘制结束标记
  if (actualEndMarker !== MARKER_TYPES.none && absolutePoints.length >= 2) {
    const lastIndex = absolutePoints.length - 1;
    drawMarker(
      context,
      absolutePoints[lastIndex],
      absolutePoints[lastIndex - 1],
      actualMarkerSize,
      actualEndMarker,
      false,
    );
  }

  context.restore();
}

// 获取标记偏移量
function getMarkerOffset(markerType, size) {
  switch (markerType) {
    case MARKER_TYPES.arrow:
      return size * 0.6;
    case MARKER_TYPES.circle:
    case MARKER_TYPES.square:
      return size * 0.5;
    default:
      return 0;
  }
}

// 绘制标记
function drawMarker(context, point, direction, size, markerType, isStart = false) {
  const angle = Math.atan2(direction.y - point.y, direction.x - point.x);

  context.save();
  context.translate(point.x, point.y);

  switch (markerType) {
    case MARKER_TYPES.arrow:
      drawArrowMarker(context, angle, size, isStart);
      break;
    case MARKER_TYPES.circle:
      drawCircleMarker(context, size);
      break;
    case MARKER_TYPES.square:
      drawSquareMarker(context, angle, size, isStart);
      break;
  }

  context.restore();
}

// 绘制箭头标记
function drawArrowMarker(context, angle, size, _isStart = false) {
  const arrowAngle = Math.PI / 6; // 30度
  // 修复箭头方向：start marker 应该指向线条方向，end marker 应该指向线条方向
  const baseAngle = angle + Math.PI;

  context.rotate(baseAngle);
  context.beginPath();
  context.moveTo(0, 0);
  context.lineTo(-size, -size * Math.tan(arrowAngle));
  context.lineTo(-size, size * Math.tan(arrowAngle));
  context.closePath();
  context.fill();
}

// 绘制圆形标记
function drawCircleMarker(context, size) {
  const radius = size * 0.4;
  context.beginPath();
  context.arc(0, 0, radius, 0, Math.PI * 2);
  context.fill();
}

// 绘制方形标记
function drawSquareMarker(context, angle, size, _isStart = false) {
  const halfSize = size * 0.4;
  context.rotate(angle);
  context.beginPath();
  context.rect(-halfSize, -halfSize, halfSize * 2, halfSize * 2);
  context.fill();
}

// 将虚线数组序列化为字符串
export function serializeDashArray(dashArray) {
  if (!dashArray || !Array.isArray(dashArray) || dashArray.length === 0) return '';

  return dashArray.join(',');
}

// 将字符串反序列化为虚线数组
export function deserializeDashArray(dashString) {
  if (!dashString || typeof dashString !== 'string') return [];

  const values = dashString.trim().split(',');
  const dashArray = [];

  for (const value of values) {
    const num = parseFloat(value);
    // 确保值是有效的正数
    if (!isNaN(num) && num >= 0) {
      dashArray.push(num);
    }
  }

  return dashArray;
}

// 将点数组序列化为字符串
export function serializePoints(points) {
  if (!points || points.length === 0) return '';

  return points.map((point) => `${point.x},${point.y}`).join(' ');
}

// 将字符串反序列化为点数组
export function deserializePoints(pointsString) {
  if (!pointsString || typeof pointsString !== 'string') return [];

  const pairs = pointsString.trim().split(' ');
  const points = [];

  for (const pair of pairs) {
    const coords = pair.split(',');
    if (coords.length === 2) {
      const x = parseFloat(coords[0]);
      const y = parseFloat(coords[1]);

      // 确保坐标是有效数字
      if (!isNaN(x) && !isNaN(y)) {
        points.push({ x, y });
      }
    }
  }

  return points;
}
